import { useState } from "react";
import { Crosshair, EyeOff, MapPin } from "lucide-react";
import type { MapDocument, MapLocation } from "../shared/types";
import type { VenueMapLogos } from "../map/useVenueMapLogos";
import { LocationMapEditor } from "./LocationMapEditor";

export function LocationFields({
  location,
  onChange,
  booth = false,
  boothHidden = false,
  document,
  logos,
  disabled = false,
}: {
  location: MapLocation;
  onChange: (location: MapLocation) => void;
  booth?: boolean;
  boothHidden?: boolean;
  document: MapDocument;
  logos?: VenueMapLogos;
  disabled?: boolean;
}) {
  const [positionOpen, setPositionOpen] = useState(false);
  const coordinates = (
    <>
      <button
        type="button"
        className="em-button em-button-secondary em-location-position-button"
        aria-haspopup="dialog"
        disabled={disabled}
        onClick={(event) => {
          event.currentTarget.focus({ preventScroll: true });
          setPositionOpen(true);
        }}
      >
        <Crosshair size={17} aria-hidden="true" />
        Position on map
      </button>
      <p className="em-field-help">
        Choose a point on a top-down map, or enter precise coordinates below.
      </p>
      <div className="em-form-grid">
        <label>
          Horizontal position <span className="em-field-unit">%</span>
          <input
            type="number"
            min={0}
            max={100}
            step={0.1}
            value={Math.round(location.x * 10000) / 100}
            onChange={(event) =>
              onChange({
                ...location,
                x: Math.max(
                  0,
                  Math.min(1, Number(event.currentTarget.value) / 100),
                ),
              })
            }
          />
        </label>
        <label>
          Vertical position <span className="em-field-unit">%</span>
          <input
            type="number"
            min={0}
            max={100}
            step={0.1}
            value={Math.round(location.y * 10000) / 100}
            onChange={(event) =>
              onChange({
                ...location,
                y: Math.max(
                  0,
                  Math.min(1, Number(event.currentTarget.value) / 100),
                ),
              })
            }
          />
        </label>
      </div>
      <p className="em-muted">
        Positions are measured from the top-left corner of the map.
        {booth &&
          " Booth pins automatically anchor to their booth’s corner; these coordinates describe the location used by integrations and navigation."}
      </p>
    </>
  );
  return (
    <div className="em-field-stack em-location-fields">
      <div className="em-info-line">
        <MapPin size={17} />
        <code>{location.id}</code>
        {(location.hidden || boothHidden) && (
          <span className="em-hidden-status">
            <EyeOff size={13} aria-hidden="true" />
            {location.hidden ? "Hidden" : "Hidden with booth"}
          </span>
        )}
      </div>
      <label>
        Location label
        <input
          maxLength={200}
          aria-describedby={`location-label-help-${location.id}`}
          value={location.label}
          onChange={(event) =>
            onChange({ ...location, label: event.currentTarget.value })
          }
        />
      </label>
      <p id={`location-label-help-${location.id}`} className="em-field-help">
        Shown in map search and pin details.
      </p>
      <div className="em-field-group">
        <label className="em-visibility-toggle">
          <input
            type="checkbox"
            checked={!location.hidden}
            aria-describedby={`location-visibility-help-${location.id}`}
            onChange={(event) =>
              onChange({ ...location, hidden: !event.currentTarget.checked })
            }
          />
          <span>Show location on map</span>
        </label>
        <p
          id={`location-visibility-help-${location.id}`}
          className="em-field-help"
        >
          {booth
            ? "Hides this location’s marker and pins without hiding its booth."
            : "Hides this place’s marker, label and pins."}
          {boothHidden &&
            " Its booth is hidden, so the location stays off the map until the booth is shown."}
        </p>
      </div>
      {booth ? (
        <details className="em-disclosure">
          <summary>Advanced position</summary>
          <div className="em-disclosure-body em-field-stack">{coordinates}</div>
        </details>
      ) : (
        coordinates
      )}
      {positionOpen && !disabled && (
        <LocationMapEditor
          key={location.id}
          document={document}
          location={location}
          logos={logos}
          booth={booth}
          onClose={() => setPositionOpen(false)}
          onApply={(position) => {
            onChange({ ...location, ...position });
            setPositionOpen(false);
          }}
        />
      )}
    </div>
  );
}
